import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { configs } from "configs";
import { redirectExternal, removeAppCredentials } from "configs/utils";
import {
  APP_CLIENT_STORAGE_KEY,
  APP_UID_STORAGE_KEY,
  AUTH_TOKEN_COOKIE_KEY,
} from "constants";
import { getCookie } from "utils/cookie";
import { logout } from "./appSlice";
import { getAppLanguage } from "helpers/getAppLanguage";
import { getLocalizedPath } from "helpers/getLocalizedPath";

const authHeader = (auth) => ({
  "access-token": auth ? auth : getCookie(AUTH_TOKEN_COOKIE_KEY),
});

const getErrorFromResponse = (response) => {
  return {
    ...response,
    error: {
      status: response.error?.originalStatus || response.error?.status,
      message:
      (Array.isArray(response?.error?.data?.errors) ? 
      response?.error?.data?.errors?.join(", ") :
      response?.error?.data?.errors 
    )
    || Object.values(response?.error?.data).join(", ")
    || "Network error",
    },
  };
};

export const getHeaders = () => ({
  prepareHeaders: (headers, { getState }) => {
    const cookieToken = getCookie(AUTH_TOKEN_COOKIE_KEY);
    const stateToken = getState().app.auth.token || cookieToken;
    const authHeaders =
      stateToken && typeof stateToken !== "boolean"
        ? authHeader(stateToken)
        : {};
    [APP_CLIENT_STORAGE_KEY, APP_UID_STORAGE_KEY].forEach((key) => {
      const value = getCookie(key);
      if (value) headers.set(key, value);
    });
    Object.entries(authHeaders).forEach(([key, value]) => {
      if (value) headers.set(key, value);
    });
    const language = getAppLanguage();
    if (language?.locale && language.locale !== "en") {
      headers.set("locale", language.locale);
    }
    return headers;
  },
});

const baseQuery = () =>
  fetchBaseQuery({
    baseUrl: configs.apiBaseURL(),
    ...getHeaders(),
    credentials: undefined,
  });

const parentApi = createApi({
  reducerPath: "parentApi",
  baseQuery: async (args, api, extraOptions) => {
    try {
      let result = await baseQuery()(args, api, extraOptions);
      if (result.error) {
        if (
          result.error.status === 401 ||
          result?.error?.originalStatus === 401
        ) {
          removeAppCredentials(null, true);
          api.dispatch(logout(true));
          api.dispatch(parentApi.util.resetApiState());
          redirectExternal(getLocalizedPath("/admin/login"))
        } else {
          return getErrorFromResponse(result);
        }
      }
      return result;
    } catch (e) {
      return getErrorFromResponse(e);
    }
  },
  endpoints: (builder) => ({
    getUserDetail: builder.query({
      query: () => `/users/current`,
    }),
    getTenantDetail: builder.query({
      query: () => `/tenants/current`,
    }),
    getUserPrivileges: builder.query({
      query: (id) => `/users/${id}/permissions`,
      transformResponse: (response) => {
        const result = [];
        if (response?.permissions) {
          for (const [key, attributes] of Object.entries(
            response?.permissions
          )) {
            for (const [attribute, value] of Object.entries(attributes)) {
              const transformedRow = { [`${key}::${attribute}`]: value };
              result.push(transformedRow);
            }
          }
        }
        return { ...response, permissions: { ...result } };
      },
    }),
  }),
});

export default parentApi;
